import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { AlertTriangle, LogOut, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Sticky red banner that renders on every page while an admin is
 * impersonating another user. Reads meta from localStorage so it
 * survives page reloads. Clicking Exit calls the AuthContext
 * exitImpersonation() helper which swaps the admin token back in
 * and re-fetches /auth/me.
 */
export default function ImpersonationBanner() {
  const { exitImpersonation } = useAuth();
  const [meta, setMeta] = useState(null);
  const [exiting, setExiting] = useState(false);

  // Poll localStorage each mount + on every route change to keep the
  // banner in sync with token swaps. Using a `storage` listener would
  // miss same-tab writes, so we also refresh on the pathname change.
  useEffect(() => {
    const refresh = () => {
      const raw = localStorage.getItem('impersonation_meta');
      if (!raw) {
        setMeta(null);
        return;
      }
      try {
        setMeta(JSON.parse(raw));
      } catch {
        setMeta(null);
      }
    };
    refresh();
    const t = setInterval(refresh, 2000);
    window.addEventListener('storage', refresh);
    return () => {
      clearInterval(t);
      window.removeEventListener('storage', refresh);
    };
  }, []);

  if (!meta) return null;

  const onExit = async () => {
    setExiting(true);
    try {
      await exitImpersonation();
      toast.success('Exited impersonation — back to your admin session');
      setMeta(null);
      // Route back to admin users. A hard nav guarantees stale RequireAuth
      // gates re-evaluate against the restored admin token.
      window.location.href = '/admin/users';
    } catch (e) {
      toast.error('Could not exit cleanly — refreshing');
      window.location.reload();
    } finally {
      setExiting(false);
    }
  };

  return (
    <div
      className="w-full bg-red-600 text-white flex items-center justify-between px-4 py-2 shadow-lg sticky top-0 z-[100]"
      data-testid="impersonation-banner"
    >
      <div className="flex items-center gap-2 text-sm min-w-0">
        <AlertTriangle className="h-4 w-4 flex-shrink-0" />
        <span className="font-semibold">Viewing as:</span>
        <span className="truncate" data-testid="impersonation-target-email">{meta.target_email}</span>
        <span className="hidden sm:inline text-red-200 text-xs">
          ({meta.target_role}) &middot; expires {new Date(meta.expires_at).toLocaleTimeString()}
        </span>
      </div>
      <Button
        size="sm"
        variant="secondary"
        onClick={onExit}
        disabled={exiting}
        className="bg-white text-red-700 hover:bg-red-50"
        data-testid="impersonation-exit-btn"
      >
        {exiting ? (
          <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Exiting…</>
        ) : (
          <><LogOut className="h-3 w-3 mr-1" /> Exit impersonation</>
        )}
      </Button>
    </div>
  );
}
